import { Tag } from "@carbon/react";
import { TableColumns } from "@flaminem/ui-components";
import { capitalize } from "@flaminem/utils";
import { useParams } from "react-router";

import { Scope, ScopeResource } from "src/authentication/PermissionsProvider.tsx";
import { usePermissions } from "src/authentication/usePermissions.ts";
import { CaseTypeTag } from "src/components/CaseTypeTag";
import { useDynamicViewSchema } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider.tsx";
import { HeadlessWidgetRenderer } from "src/dynamic-view-renderer/headlessWidgets";
import { useGetBlacklistElementOriginLabel } from "src/hooks/blacklist";
import { useFormatPropertyValue } from "src/hooks/property/useFormatProperty";
import { useT } from "src/internationalization";
import { useListSchema } from "src/pages/Blacklists/useListSchema.ts";
import { BlacklistElement } from "src/types/blacklist";

import { useListMetaData } from "../useListMetaData";

import { BlacklistActions } from "./BlacklistActions";

type UseGetBlacklistsTableColumnsProps = {
  onStatusChanged: () => void;
  onElementUpdated: () => void;

  searchTerm: string;
};

export const useGetBlacklistsTableColumns = ({
  onStatusChanged,
  onElementUpdated,
  searchTerm,
}: UseGetBlacklistsTableColumnsProps) => {
  const { listSchema } = useListSchema();
  const params = useParams<{ code: string }>();
  const { blacklists, headlessWidgets } = useDynamicViewSchema();
  const { hasPartialPermission } = usePermissions();

  const t = useT();
  const listMetaData = useListMetaData();
  const getBlacklistElementOriginLabel = useGetBlacklistElementOriginLabel();
  const { caseTypeDefinitions } = useDynamicViewSchema();
  const formatPropertyValue = useFormatPropertyValue();

  const blacklist = blacklists?.find((b) => b.blacklistCode === params.code);

  if (blacklist && blacklist.blacklistTable && blacklist.blacklistTable.columns) {
    const dynamicColumns: TableColumns<BlacklistElement> = (
      blacklist!.blacklistTable.columns ?? []
    ).reduce((acc, column) => {
      const widget = headlessWidgets.find((w) => w.code === column.headlessWidgetCode);
      if (!widget) return acc;

      return {
        ...acc,
        [column.headlessWidgetCode]: {
          label: column.headerLabel,
          flexGrow: column.flexGrow ?? (column.width && column.width !== 1 ? 0 : 1),
          flexShrink: column.flexShrink ?? (column.width && column.width !== 1 ? 0 : 1),
          width: column.width || 1,
          className: "text-sm",
          render: (row) => (
            <HeadlessWidgetRenderer
              widget={widget}
              searchTerm={searchTerm}
              data={{
                root: row,
                elements: row.elements,
                propertyValues: row.properties,
                caseTypeDefinitions: caseTypeDefinitions,
              }}
            />
          ),
        },
      };
    }, {});

    const columns = {
      ...dynamicColumns,
      ...(hasPartialPermission(ScopeResource.BLACKLIST_ELEMENT, Scope.DISABLE, Scope.EDIT) && {
        actions: {
          label: "",
          width: 100,
          render: (row) => (
            <BlacklistActions
              blacklistElementRecord={row}
              onStatusChange={onStatusChanged}
              onElementUpdated={onElementUpdated}
            />
          ),
        },
      }),
    };

    return columns;
  } else {
    const columns = {
      ...(listSchema?.properties ?? []).reduce((obj, prop) => {
        obj[prop.name] = {
          label: capitalize(prop.name),
          flexGrow: 1,
          flexShrink: 1,
          width: 1,
          className: "text-sm",
          render: (row: BlacklistElement) => {
            const property = row.properties.find((p) => p.property.code === prop.code);
            return property && formatPropertyValue(property);
          },
        };
        return obj;
      }, {}),
      ...listMetaData
        .filter(({ header }) => !!header)
        .reduce((obj, prop) => {
          obj[prop.name] = {
            label: capitalize(prop.header ?? prop.name),
            flexGrow: 1,
            flexShrink: 1,
            width: 1,
            className: "text-sm",
            render: (row: BlacklistElement) => {
              switch (prop.name) {
                case "status":
                  return (
                    <Tag type={row[prop.name] ? "green" : "red"}>
                      {row[prop.name]
                        ? t("page.blacklists.table.status.active")
                        : t("page.blacklists.table.status.inactive")}
                    </Tag>
                  );
                case "origin":
                  return row[prop.name] ? getBlacklistElementOriginLabel(row[prop.name]) : "";
                case "sourceCaseType.code": {
                  const caseTypeCode = row["sourceCaseType"]?.code || "";
                  return (
                    <CaseTypeTag
                      caseTypeCode={caseTypeCode}
                      caseTypeDefinitions={caseTypeDefinitions}
                      fallbackType="magenta"
                    />
                  );
                }
                default:
                  return row[prop.name];
              }
            },
          };
          return obj;
        }, {}),
      ...(hasPartialPermission(ScopeResource.BLACKLIST_ELEMENT, Scope.DISABLE, Scope.EDIT) && {
        actions: {
          label: "",
          width: 100,
          render: (row: BlacklistElement) => (
            <BlacklistActions
              blacklistElementRecord={row}
              onStatusChange={onStatusChanged}
              onElementUpdated={onElementUpdated}
            />
          ),
        },
      }),
    };

    return columns;
  }
};
